import { useState, useCallback, useMemo } from 'react';

/**
 * useItemSelection Hook
 * Unified facility and material selection management
 *
 * Provides a consistent interface for selecting facilities or materials
 * with category/product grouping, search filtering, and selection tracking
 *
 * @param {Object} config - Configuration object
 * @param {string} config.type - Selection type: 'facility' | 'material'
 * @param {Object} config.dataSource - Data source configuration
 * @param {Array} config.dataSource.categories - Category/Product list
 * @param {Object} config.dataSource.itemsByCategory - Items grouped by category
 * @param {Object} config.initialState - Initial state (optional)
 * @param {Array} config.initialState.selectedCategories - Pre-selected categories
 * @param {Array} config.initialState.selectedItems - Pre-selected items
 * @returns {Object} Selection state and management functions
 */
const useItemSelection = (config) => {
  const {
    type, // 'facility' | 'material'
    dataSource = {},
    initialState = {},
  } = config;

  const {
    categories = [], // Facility categories OR Material products
    itemsByCategory = {}, // Facilities by category OR Materials by product
  } = dataSource;

  // Category/Product selection state
  const [selectedCategories, setSelectedCategories] = useState(
    initialState.selectedCategories || []
  );

  // Item selection state
  const [selectedItems, setSelectedItems] = useState(
    initialState.selectedItems || []
  );

  // Search text state
  const [categorySearchText, setCategorySearchText] = useState('');
  const [itemSearchText, setItemSearchText] = useState('');

  /**
   * Get configuration based on type (facility or material)
   * Returns type-specific field names and configurations
   */
  const typeConfig = useMemo(() => {
    if (type === 'facility') {
      return {
        // Modal configuration
        title: '설비 선택',
        categoryTitle: '설비 분류',
        categorySearchPlaceholder: '분류를 검색하세요',
        searchPlaceholder: '설비명을 입력하세요',
        listTitle: '설비 등록',
        emptyMessage: '선택한 설비가 없어요',
        noSelectionMessage: '분류를 먼저 선택하세요',
        color: '#f57c00',
        chipBgColor: '#fff3e0',

        // Field mappings
        itemKey: 'facilityCd',
        itemGroupKey: 'categoryCd',
        itemName: 'facilityNm',
        itemSecondary: 'categoryNm',

        // Alternative field names for compatibility
        altItemKey: ['machineId', 'chaebun'],
        altItemName: ['machineNm', 'machineName'],
        altItemGroupKey: ['machineType'],
      };
    } else if (type === 'material') {
      return {
        // Modal configuration
        title: '물질 선택',
        categoryTitle: '제품명',
        categorySearchPlaceholder: '제품을 검색하세요',
        searchPlaceholder: '물질명을 입력하세요',
        listTitle: '물질 등록',
        emptyMessage: '선택한 물질이 없어요',
        noSelectionMessage: '제품을 먼저 선택하세요',
        color: '#1976d2',
        chipBgColor: '#e3f2fd',

        // Field mappings - 실제 DB 구조에 맞게 수정
        itemKey: 'hbdId', // 물질 ID (HBD_ID) - 고유 식별자
        itemGroupKey: 'hbmId', // 제품 ID (HBM_ID) - 카테고리 역할
        itemName: 'substanceName', // 물질명 (SUBSTANCE_NAME)
        itemSecondary: 'productName', // 제품명 (PRODUCT_NAME)

        // Alternative field names for compatibility
        altItemKey: ['materialId', 'MATERIAL_CD'],
        altItemName: ['materialNm', 'materialName', 'MATERIAL_NM'],
        altItemGroupKey: ['productId', 'PRODUCT_ID'],
      };
    }
    return null;
  }, [type]);

  /**
   * Get field value supporting multiple field name variations
   * @param {Object} item - The item object
   * @param {string} primaryKey - Primary field name
   * @param {Array} altKeys - Alternative field names
   * @returns {any} Field value
   */
  const getFieldValue = useCallback((item, primaryKey, altKeys = []) => {
    if (!item) return '';
    // primaryKey로 먼저 확인
    if (
      item[primaryKey] !== undefined &&
      item[primaryKey] !== null &&
      item[primaryKey] !== ''
    ) {
      return item[primaryKey];
    }
    // altKeys에서 값이 있는 첫 번째 키를 찾아서 그 값을 반환
    for (const key of altKeys) {
      if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
        return item[key];
      }
    }
    return '';
  }, []);

  /**
   * Get item identifier (handles multiple field name variations)
   */
  const getItemId = useCallback(
    (item) => {
      return getFieldValue(item, typeConfig?.itemKey, typeConfig?.altItemKey);
    },
    [typeConfig, getFieldValue]
  );

  /**
   * Get item group identifier (category or product)
   */
  const getItemGroupId = useCallback(
    (item) => {
      return getFieldValue(
        item,
        typeConfig?.itemGroupKey,
        typeConfig?.altItemGroupKey
      );
    },
    [typeConfig, getFieldValue]
  );

  /**
   * Get item display name
   */
  const getItemName = useCallback(
    (item) => {
      return getFieldValue(item, typeConfig?.itemName, typeConfig?.altItemName);
    },
    [typeConfig, getFieldValue]
  );

  /**
   * Filter items by selected categories and search text
   * Returns items that belong to selected categories/products
   */
  const filteredItems = useMemo(() => {
    if (selectedCategories.length === 0) return [];

    let result = [];

    selectedCategories.forEach((category) => {
      const items = itemsByCategory[category.value] || [];
      items.forEach((item) => {
        // Normalize item data based on type
        const normalizedItem = {
          ...item,
          [typeConfig.itemKey]: getItemId(item),
          [typeConfig.itemGroupKey]: getItemGroupId(item) || category.value,
          [typeConfig.itemName]: getItemName(item),
          [typeConfig.itemSecondary]: category.label,
        };

        // Add type-specific fields
        if (type === 'facility') {
          normalizedItem.facilityCd =
            normalizedItem.facilityCd || item.chaebun || '';
          normalizedItem.facilityNm =
            normalizedItem.facilityNm || item.machineName || '';
          normalizedItem.categoryCd =
            normalizedItem.categoryCd || category.value;
          normalizedItem.categoryNm =
            normalizedItem.categoryNm || category.label;
          // Keep original fields for compatibility
          normalizedItem.chaebun = item.chaebun;
          normalizedItem.machineName = item.machineName;
        } else if (type === 'material') {
          normalizedItem.hbdId = normalizedItem.hbdId || item.hbdId || '';
          normalizedItem.substanceName =
            normalizedItem.substanceName || item.substanceName || '';
          normalizedItem.hbmId =
            normalizedItem.hbmId || item.hbmId || category.value;
          normalizedItem.productName =
            normalizedItem.productName || item.productName || category.label;
          // 호환성을 위한 필드 추가
          normalizedItem.materialId = normalizedItem.hbdId;
          normalizedItem.materialNm = normalizedItem.substanceName;
          normalizedItem.materialName = normalizedItem.substanceName;
          normalizedItem.productId = normalizedItem.hbmId;
          // 대문자 필드 유지 (호환성)
          normalizedItem.MATERIAL_CD = normalizedItem.hbdId;
          normalizedItem.MATERIAL_NM = normalizedItem.substanceName;
          normalizedItem.PRODUCT_ID = normalizedItem.hbmId;
          normalizedItem.PRODUCT_NAME = normalizedItem.productName;
          // 원본 필드 유지
          normalizedItem.mapId = item.mapId;
          normalizedItem.subJobId = item.subJobId;
        }

        result.push(normalizedItem);
      });
    });

    // Apply search filter
    if (itemSearchText) {
      const searchLower = itemSearchText.toLowerCase();
      result = result.filter((item) => {
        const itemName = getItemName(item).toLowerCase();
        return itemName.includes(searchLower);
      });
    }

    return result;
  }, [
    selectedCategories,
    itemsByCategory,
    itemSearchText,
    type,
    typeConfig,
    getItemId,
    getItemGroupId,
    getItemName,
  ]);

  /**
   * Filter categories by search text
   */
  const filteredCategories = useMemo(() => {
    if (!categorySearchText) return categories;

    const searchLower = categorySearchText.toLowerCase();
    return categories.filter((cat) =>
      cat.label.toLowerCase().includes(searchLower)
    );
  }, [categories, categorySearchText]);

  /**
   * Check if two items are the same (handles multiple field name variations)
   * 아이템 고유 ID만 비교 (chaebun 또는 hbdId가 이미 고유하므로 group 비교 불필요)
   */
  const isSameItem = useCallback(
    (item1, item2) => {
      const id1 = getItemId(item1);
      const id2 = getItemId(item2);

      // ID만 비교 (chaebun, hbdId 등이 이미 고유 식별자이므로)
      return (
        id1 === id2 &&
        id1 !== '' &&
        id2 !== '' &&
        id1 !== null &&
        id2 !== null &&
        id1 !== undefined &&
        id2 !== undefined
      );
    },
    [getItemId]
  );

  /**
   * Toggle category/product selection
   * When deselecting, also removes items from that category/product
   */
  const toggleCategory = useCallback(
    (category) => {
      setSelectedCategories((prev) => {
        const exists = prev.find((c) => c.value === category.value);
        if (exists) {
          // Remove category and its items
          setSelectedItems((prevItems) =>
            prevItems.filter((item) => {
              const itemGroup = getItemGroupId(item);
              return itemGroup !== category.value;
            })
          );
          return prev.filter((c) => c.value !== category.value);
        }
        return [...prev, category];
      });
    },
    [getItemGroupId]
  );

  /**
   * Toggle item selection (add or remove)
   */
  const toggleItem = useCallback(
    (item) => {
      setSelectedItems((prev) => {
        const exists = prev.find((i) => isSameItem(i, item));
        if (exists) {
          // Remove item
          return prev.filter((i) => !isSameItem(i, item));
        }
        // Add item
        return [...prev, item];
      });
    },
    [isSameItem]
  );

  /**
   * Check if an item is selected
   */
  const isItemSelected = useCallback(
    (item) => {
      return selectedItems.some((i) => isSameItem(i, item));
    },
    [selectedItems, isSameItem]
  );

  /**
   * Check if a category is selected
   */
  const isCategorySelected = useCallback(
    (category) => {
      return selectedCategories.some((c) => c.value === category.value);
    },
    [selectedCategories]
  );

  /**
   * Get count of items in a category
   */
  const getCategoryItemCount = useCallback(
    (category) => {
      return (itemsByCategory[category.value] || []).length;
    },
    [itemsByCategory]
  );

  /**
   * Clear all selections
   */
  const clearAll = useCallback(() => {
    setSelectedItems([]);
    setSelectedCategories([]);
  }, []);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setSelectedCategories(initialState.selectedCategories || []);
    setSelectedItems(initialState.selectedItems || []);
    setCategorySearchText('');
    setItemSearchText('');
  }, [initialState]);

  /**
   * Load existing selections from saved data
   * Converts saved data format to selection format
   */
  const loadFromSavedData = useCallback(
    (savedItems, allCategories, allItemsByCategory) => {
      if (!savedItems || savedItems.length === 0) {
        clearAll();
        return;
      }

      // Extract unique category IDs from saved items
      const categoryIds = new Set(
        savedItems.map((item) => getItemGroupId(item)).filter(Boolean)
      );

      // Find category objects
      const categoriesToSelect = allCategories.filter((cat) =>
        categoryIds.has(cat.value)
      );

      // Convert saved items to normalized format
      const itemsToSelect = savedItems.map((item) => {
        const groupId = getItemGroupId(item);
        const category = categoriesToSelect.find((c) => c.value === groupId);

        return {
          ...item,
          [typeConfig.itemKey]: getItemId(item),
          [typeConfig.itemGroupKey]: groupId,
          [typeConfig.itemName]: getItemName(item),
          [typeConfig.itemSecondary]: category?.label || '',
        };
      });

      setSelectedCategories(categoriesToSelect);
      setSelectedItems(itemsToSelect);
    },
    [typeConfig, getItemId, getItemGroupId, getItemName, clearAll]
  );

  /**
   * Get valid selected items (filters out items with missing required fields)
   */
  const validSelectedItems = useMemo(() => {
    return selectedItems.filter((item) => {
      const id = getItemId(item);
      const name = getItemName(item);
      return id && name;
    });
  }, [selectedItems, getItemId, getItemName]);

  /**
   * Get modal configuration object
   * Returns complete configuration for rendering the selection modal
   */
  const getModalConfig = useCallback(() => {
    if (!typeConfig) return null;

    return {
      ...typeConfig,
      // Categories
      categories: filteredCategories,
      selectedCategories,
      categorySearchText,
      setCategorySearchText,
      toggleCategory,
      getCategoryCount: getCategoryItemCount,

      // Items
      items: filteredItems,
      selectedItems: validSelectedItems,
      searchText: itemSearchText,
      setSearchText: setItemSearchText,
      toggleItem,
      isItemSelected,
      isCategorySelected,

      // Actions
      clearAll,
    };
  }, [
    typeConfig,
    filteredCategories,
    selectedCategories,
    categorySearchText,
    toggleCategory,
    getCategoryItemCount,
    filteredItems,
    validSelectedItems,
    itemSearchText,
    toggleItem,
    isItemSelected,
    isCategorySelected,
    clearAll,
  ]);

  return {
    // State
    selectedCategories,
    selectedItems,
    validSelectedItems,
    filteredItems,
    filteredCategories,
    categorySearchText,
    itemSearchText,

    // Setters
    setSelectedCategories,
    setSelectedItems,
    setCategorySearchText,
    setItemSearchText,

    // Actions
    toggleCategory,
    toggleItem,
    isItemSelected,
    isCategorySelected,
    getCategoryItemCount,
    clearAll,
    clearSelections: clearAll, // Alias for clearAll
    reset,
    loadFromSavedData,

    // Configuration
    getModalConfig,
    typeConfig,

    // Utility
    getItemId,
    getItemGroupId,
    getItemName,
    isSameItem,
  };
};

export default useItemSelection;
